import { type CSSProperties, type ReactNode, useMemo } from 'react';

/**
 * Corner identifier for the cut-corner notch.
 * `tl` = top-left, `tr` = top-right, `bl` = bottom-left, `br` = bottom-right.
 */
export type NotchedCorner = 'tl' | 'tr' | 'bl' | 'br';

export interface NotchedPanelProps {
  /** Which corners receive a cut notch. Default: `['tl', 'br']`. */
  corners?: readonly NotchedCorner[];
  /** Notch size in pixels. Default: 18. */
  notch?: number;
  /** Whether to render tick-marks along the top accent strip. Default: false. */
  ticks?: boolean;
  /** CSS color for the accent strip + tick marks. Default: `var(--cd-sig-iris)`. */
  accent?: string;
  /** Additional class names merged onto the outer wrapper. */
  className?: string;
  /** Optional explicit role for a11y consumers (e.g. `'region'`). */
  role?: string;
  /** Accessible label for consumers that pass `role`. */
  'aria-label'?: string;
  children?: ReactNode;
  /** Test hook for RTL queries. Default: `'notched-panel'`. */
  testId?: string;
}

const DEFAULT_CORNERS: readonly NotchedCorner[] = ['tl', 'br'] as const;
const TICK_COUNT = 24;

/**
 * Build a `clip-path: polygon(...)` value that cuts a diagonal notch at each
 * corner listed in `corners`. Coordinates are emitted in clockwise order
 * starting at top-left so the polygon closes cleanly for any subset.
 *
 * Karpathy note: there are simpler CSS-mask approaches with `clip-path: inset`
 * + `--corner-size` custom-corners, but browser support still lags. An explicit
 * polygon is deterministic and snapshot-friendly.
 */
export function buildNotchedClipPath(
  corners: readonly NotchedCorner[],
  notch: number,
): string {
  const hasTL = corners.includes('tl');
  const hasTR = corners.includes('tr');
  const hasBR = corners.includes('br');
  const hasBL = corners.includes('bl');
  const n = `${notch}px`;

  const points: string[] = [];
  // top edge, left → right
  points.push(hasTL ? `${n} 0` : '0 0');
  points.push(hasTR ? `calc(100% - ${n}) 0` : '100% 0');
  if (hasTR) points.push(`100% ${n}`);
  // right edge, top → bottom
  points.push(hasBR ? `100% calc(100% - ${n})` : '100% 100%');
  if (hasBR) points.push(`calc(100% - ${n}) 100%`);
  // bottom edge, right → left
  points.push(hasBL ? `${n} 100%` : '0 100%');
  if (hasBL) points.push(`0 calc(100% - ${n})`);
  // left edge, bottom → top
  if (hasTL) points.push(`0 ${n}`);

  return `polygon(${points.join(', ')})`;
}

/**
 * `NotchedPanel` — surface primitive for Workspace-V2 signature panels.
 * Cycle 101 sprint-5a (FORGE Lane, sprint.md §Sprint-5 §Sub-track 5a).
 *
 * Renders an accent strip + optional tick-marks + cut-corner silhouette
 * around arbitrary children. The component is presentational — all layout
 * and scroll decisions stay with the consumer.
 *
 * Reuse target: sprint-5b ports 12 WM modules and reuses this shell to keep
 * the lane visual identity consistent with FORGE.
 */
export function NotchedPanel({
  corners = DEFAULT_CORNERS,
  notch = 18,
  ticks = false,
  accent = 'var(--cd-sig-iris)',
  className,
  role,
  children,
  testId = 'notched-panel',
  ...rest
}: NotchedPanelProps) {
  const clipPath = useMemo(() => buildNotchedClipPath(corners, notch), [corners, notch]);

  const panelStyle: CSSProperties = {
    clipPath,
    // Custom props are typed as string via `as const`-style cast-free keys.
    ['--notched-accent' as string]: accent,
  };

  return (
    <div
      data-testid={testId}
      data-corners={[...corners].sort().join(' ')}
      data-notch={notch}
      data-ticks={ticks ? '1' : '0'}
      role={role}
      aria-label={rest['aria-label']}
      style={panelStyle}
      className={[
        'relative isolate bg-card/90 text-foreground ring-1 ring-inset ring-border/60',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
        style={{ background: 'var(--notched-accent)' }}
      />
      {ticks ? (
        <div
          aria-hidden="true"
          data-testid={`${testId}-ticks`}
          className="pointer-events-none absolute inset-x-0 top-[2px] flex h-[6px]"
        >
          {Array.from({ length: TICK_COUNT }, (_, i) => (
            <span
              key={i}
              className="flex-1 border-l border-[color:var(--notched-accent)] opacity-30 last:border-r"
            />
          ))}
        </div>
      ) : null}
      {children}
    </div>
  );
}
